import { apiService } from '../../../components/api/apiService';
import { logDataModels } from './dataModelsLogger';

const CONNECTION = 'backend_py';
const BASE = '/api/v1/reporting/data-models/audit';

export type AuditStream = 'workspace' | 'catalog' | 'asset' | 'policy';

export interface DataModelsAuditEvent {
  eventId: string;
  stream: AuditStream;
  action: string;
  outcome: string;
  summary: string;
  actorId: string;
  actorName: string;
  actorEmail: string;
  occurredAt: string;
  workspaceId: string;
  workspaceName: string;
  catalogId: string;
  catalogName: string;
  assetId: string;
  assetName: string;
  assetKind: string;
  policyId: string;
  policyName: string;
  policyType: string;
  policyLevel: string;
  policyStatus: string;
  payload: Record<string, unknown>;
}

export interface DataModelsAuditQuery {
  streams?: AuditStream[];
  workspaceId?: string;
  catalogId?: string;
  assetId?: string;
  policyId?: string;
  action?: string;
  actor?: string;
  policyType?: string;
  policyLevel?: string;
  outcome?: string;
  q?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

export interface DataModelsAuditPage {
  items: DataModelsAuditEvent[];
  nextCursor: string | null;
}

function serviceErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as {
    response?: { data?: { error?: { message?: string }; message?: string } };
    message?: string;
  };
  return axiosErr.response?.data?.error?.message || axiosErr.response?.data?.message || axiosErr.message || fallback;
}

function asParams(query: DataModelsAuditQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (query.streams?.length) params.streams = query.streams.join(',');
  if (query.workspaceId) params.workspaceId = query.workspaceId;
  if (query.catalogId) params.catalogId = query.catalogId;
  if (query.assetId) params.assetId = query.assetId;
  if (query.policyId) params.policyId = query.policyId;
  if (query.action) params.action = query.action;
  if (query.actor) params.actor = query.actor;
  if (query.policyType) params.policyType = query.policyType;
  if (query.policyLevel) params.policyLevel = query.policyLevel;
  if (query.outcome) params.outcome = query.outcome;
  if (query.q) params.q = query.q;
  if (query.from) params.from = query.from;
  if (query.to) params.to = query.to;
  if (query.cursor) params.cursor = query.cursor;
  if (query.limit) params.limit = query.limit;
  return params;
}

function asEvent(row: Partial<DataModelsAuditEvent>): DataModelsAuditEvent | null {
  if (!row?.eventId) return null;
  return {
    eventId: row.eventId,
    stream: (row.stream || 'workspace') as AuditStream,
    action: row.action || '',
    outcome: row.outcome || 'succeeded',
    summary: row.summary || '',
    actorId: row.actorId || '',
    actorName: row.actorName || '',
    actorEmail: row.actorEmail || '',
    occurredAt: row.occurredAt || '',
    workspaceId: row.workspaceId || '',
    workspaceName: row.workspaceName || '',
    catalogId: row.catalogId || '',
    catalogName: row.catalogName || '',
    assetId: row.assetId || '',
    assetName: row.assetName || '',
    assetKind: row.assetKind || '',
    policyId: row.policyId || '',
    policyName: row.policyName || '',
    policyType: row.policyType || '',
    policyLevel: row.policyLevel || '',
    policyStatus: row.policyStatus || '',
    payload: row.payload && typeof row.payload === 'object' ? row.payload : {},
  };
}

export async function listDataModelsAudit(query: DataModelsAuditQuery): Promise<DataModelsAuditPage> {
  logDataModels('app', 'audit_load', { streams: query.streams, workspaceId: query.workspaceId, catalogId: query.catalogId, assetId: query.assetId });
  try {
    const data = (await apiService.get(BASE, asParams(query), CONNECTION)) as { items?: unknown; nextCursor?: string | null };
    const items = Array.isArray(data?.items)
      ? data.items.map((item) => asEvent(item as Partial<DataModelsAuditEvent>)).filter((item): item is DataModelsAuditEvent => Boolean(item))
      : [];
    logDataModels('app', 'audit_load_ok', { count: items.length, hasMore: Boolean(data?.nextCursor) });
    return { items, nextCursor: data?.nextCursor || null };
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to load activity');
    logDataModels('error', 'audit_load_failed', { message });
    throw new Error(message);
  }
}

export async function exportDataModelsAudit(query: DataModelsAuditQuery): Promise<Blob> {
  logDataModels('app', 'audit_export', { streams: query.streams, workspaceId: query.workspaceId });
  try {
    const data = await apiService.get(`${BASE}/export`, asParams(query), CONNECTION);
    const text = typeof data === 'string' ? data : String(data ?? '');
    logDataModels('app', 'audit_export_ok', { bytes: text.length });
    return new Blob([text], { type: 'text/csv;charset=utf-8' });
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to export activity');
    logDataModels('error', 'audit_export_failed', { message });
    throw new Error(message);
  }
}
